/* global React, Card, Badge, Button, Select, useState, useMemo */
// ============================================================
// AVSTEMMINGSMAPPA — hovedbokslinjene og områdehodet
// ============================================================
// Den generiske kontotabellen sier hva saldoen ER. Linjene her sier hva den
// BESTÅR AV: periodens bevegelse post for post, og hvor gamle postene er.
//
// Det er forskjellen på en avstemming og en avkryssing. En konto på 84 000 som
// ikke har rørt seg på fire måneder er noe annet enn en konto på 84 000 med
// tolv posteringer i perioden, og saldoen alene skiller dem ikke.
//
// Linjene kommer fra `buildLedgerLines`, som er et utvalg over de samme
// bilagene SAF-T-eksporten bruker. Ingen egen innsamler, ingen egen oppfatning
// av hva som er bokført.
//
// Fila eide en gang en hel EGEN skjerm — `AvstAreaLedgerPage`. Alle elleve
// områdene har nå sin egen fagskjerm, og skjermen sto igjen uten en rute som
// kunne nå den. Det som ble igjen her er delene de elleve faktisk bruker:
// områdehodet og hovedbokslinjene.

const avstLedgerPeriodRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return null;
  const pad = (value) => String(value).padStart(2, '0');
  return {
    from: `${year}-${pad(monthIndex + 1)}-01`,
    to: `${year}-${pad(monthIndex + 1)}-${pad(new Date(year, monthIndex + 1, 0).getDate())}`,
  };
};

// Posteringene i perioden. Alderen regnes fra posteringsdatoen til slutten av
// perioden — en post fra januar som fortsatt ligger på en balansekonto i august
// er noe annet enn en fra forrige uke.
// Age buckets are Norwegian ids produced by ledgerLineAgeBucket and compared in logic;
// only the label shown to the user is translated.
const AVST_AGE_BUCKET_KEYS = { '0–30 dager': 'd0to30', '31–60 dager': 'd31to60', '61–90 dager': 'd61to90', 'Over 90 dager': 'over90', 'Uten dato': 'noDate' };
const avstAgeBucketLabel = (bucket) => (AVST_AGE_BUCKET_KEYS[bucket] ? t('ava.ledger.age.' + AVST_AGE_BUCKET_KEYS[bucket]) : bucket);

function AvstLedgerLines({ lines, referenceDate, accounts }) {
  const [accountFilter, setAccountFilter] = useState('');
  const [onlyOld, setOnlyOld] = useState(false);
  const rows = lines.filter((line) => {
    if (accountFilter && line.accountCode !== accountFilter) return false;
    if (onlyOld && !['61–90 dager', 'Over 90 dager'].includes(line.ageBucket)) return false;
    return true;
  });
  const buckets = new Map();
  for (const line of lines) buckets.set(line.ageBucket, (buckets.get(line.ageBucket) || 0) + line.amount);

  return <Card>
    <div className="row between wrap gap-10" style={{ alignItems: 'center' }}>
      <div className="kav-h" style={{ marginBottom: 0 }}>{t('ava.ledger.title')}</div>
      <div className="row gap-8">
        <Select value={accountFilter} onChange={(event) => setAccountFilter(event.target.value)} aria-label={t('ava.common.account')}>
          <option value="">{t('ava.ledger.allAccounts')}</option>
          {accounts.map((account) => <option key={account.accountId} value={account.accountId}>
            {account.accountId} {account.accountName}
          </option>)}
        </Select>
        <label className="avst-toggle">
          <input type="checkbox" checked={onlyOld} onChange={(event) => setOnlyOld(event.target.checked)} />
          {t('ava.ledger.onlyOld')}
        </label>
      </div>
    </div>

    {buckets.size ? <div className="row gap-8 wrap" style={{ margin: '4px 0 12px' }}>
      {[...buckets.entries()].map(([bucket, sum]) => <Badge key={bucket} variant={
        bucket === 'Over 90 dager' ? 'error' : bucket === '61–90 dager' ? 'warning' : 'muted'
      } dot>{avstAgeBucketLabel(bucket)}: {window.avstMoney(sum)}</Badge>)}
    </div> : null}

    <div className="tbl-wrap"><table className="tbl">
      <thead><tr><th>{t('ava.common.date')}</th><th>{t('ava.common.account')}</th><th>{t('ava.common.voucher')}</th><th>{t('ava.ledger.colText')}</th><th className="num">{t('ava.common.amount')}</th><th>{t('ava.ledger.colAge')}</th></tr></thead>
      <tbody>{rows.map((line) => <tr key={line.id}>
        <td style={{ whiteSpace: 'nowrap' }}>{line.date}</td>
        <td className="t-xs" style={{ fontWeight: 700 }}>{line.accountCode}</td>
        <td className="t-xs" style={{ fontWeight: 700 }}>{line.voucherNumber || line.kind}</td>
        <td>{line.description || line.voucherName}</td>
        <td className="num">{window.avstMoney(line.amount)}</td>
        <td className="t-xs" style={{ fontWeight: 700 }}>{avstAgeBucketLabel(line.ageBucket)}</td>
      </tr>)}</tbody>
    </table></div>
    {rows.length === 0 ? <div className="t-xs muted" style={{ fontWeight: 700, marginTop: 10 }}>
      {lines.length ? t('ava.ledger.noMatch') : t('ava.ledger.noLines')}
    </div> : null}
  </Card>;
}


// Toppen alle omraadeskjermene deler: hva omraadet heter, hvor mye som staar
// igjen, og veien videre til kontroll.
function AvstAreaHeader({ areaLabel, status, counts, hint, busy, locked, onSendAreaToControl }) {
  return <Card>
    <div className="row between wrap gap-14">
      <div>
        <div className="t-h3" style={{ fontSize: 16 }}>{areaLabel}</div>
        <div className="t-xs muted" style={{ fontWeight: 700, marginTop: 3 }}>
          {t('ava.area.counts', { approved: counts.godkjent, total: counts.total, deviations: counts.avvik, missing: counts.mangler })}
          {hint ? ' · ' + hint : ''}
        </div>
      </div>
      <div className="row gap-10" style={{ alignItems: 'center' }}>
        <span className={'kav-tag kav-tag-' + window.avstmCell(status).cls}>
          {window.AVST_STATUS_LABEL[status] || status}
        </span>
        {onSendAreaToControl ? <Button size="sm" variant="secondary" disabled={busy || locked} onClick={onSendAreaToControl}>
          {t('ava.area.sendToControl')}
        </Button> : null}
      </div>
    </div>
  </Card>;
}

// Posteringslista som fagskjermene deler. Henter selv om linjene ikke er sendt
// inn, slik at hver skjerm slipper aa gjenta oppslaget.
function AvstLedgerLinesForArea({ periodId, accounts, lines, range }) {
  const resolvedRange = range || avstLedgerPeriodRange(periodId);
  const codes = accounts.map((account) => account.accountId).filter(Boolean);
  const resolved = useMemo(() => {
    if (lines) return lines;
    if (!resolvedRange || !codes.length || !window.buildLedgerLines) return [];
    return window.buildLedgerLines({ from: resolvedRange.from, to: resolvedRange.to, accountCodes: codes });
  }, [periodId, codes.join(','), lines]);
  const withAge = resolved.map((line) => ({
    ...line,
    ageBucket: line.ageBucket || window.ledgerLineAgeBucket(line.date, resolvedRange?.to),
  }));
  return <AvstLedgerLines lines={withAge} referenceDate={resolvedRange?.to} accounts={accounts} />;
}


Object.assign(window, {
  AvstAreaHeader, AvstLedgerLines, AvstLedgerLinesForArea, avstLedgerPeriodRange, avstAgeBucketLabel,
});
